import React, { useState } from 'react';
import { Review } from '../types/Review';
import { format } from 'date-fns';
import ReviewDetailModal from './ReviewDetailModal';
import { 
  HighlightCountBadge, 
  PriorityBadge, 
  SourceBadge,
  ImpactScoreBadge
} from './ReviewBadges';
import '../styles/DenseReviewList.css';

interface DenseReviewListProps {
  reviews: Review[];
  onUpdate: (id: string, updates: Partial<Review>) => void;
  onDelete: (id: string) => void;
  onBulkAction?: (action: string, ids: string[]) => void;
  viewMode?: 'table' | 'cards' | 'compact' | 'highlights';
}

const DenseReviewList: React.FC<DenseReviewListProps> = ({
  reviews,
  onUpdate,
  onDelete,
  onBulkAction,
  viewMode = 'table'
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === reviews.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(reviews.map(r => r.id)));
    }
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const handleBulkAction = (action: string) => {
    if (onBulkAction && selectedIds.size > 0) {
      onBulkAction(action, Array.from(selectedIds));
      setSelectedIds(new Set());
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? 'N/A' : format(date, 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };



  const renderStars = (rating: number) => {
    return '★'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating));
  };

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortedReviews = [...reviews].sort((a, b) => {
    let compareValue = 0;
    
    switch (sortColumn) {
      case 'date':
        compareValue = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        break;
      case 'source':
        compareValue = a.source.localeCompare(b.source);
        break;
      case 'rating':
        compareValue = a.rating - b.rating;
        break;
      case 'reviewer':
        compareValue = (a.reviewer_name || '').localeCompare(b.reviewer_name || '');
        break;
      case 'highlights':
        compareValue = (a.highlights?.length || 0) - (b.highlights?.length || 0);
        break;
      case 'priority':
        const priorityOrder = { 'GOLD': 3, 'SILVER': 2, 'BRONZE': 1, '': 0 };
        const aPriority = a.metadata?.priority || '';
        const bPriority = b.metadata?.priority || '';
        compareValue = (priorityOrder[aPriority as keyof typeof priorityOrder] || 0) - 
                      (priorityOrder[bPriority as keyof typeof priorityOrder] || 0);
        break;
      default:
        compareValue = 0;
    }
    
    return sortDirection === 'asc' ? compareValue : -compareValue;
  });

  const SortableHeader = ({ column, children }: { column: string; children: React.ReactNode }) => (
    <th 
      className={`sortable ${column}-col ${sortColumn === column ? 'active' : ''}`}
      onClick={() => handleSort(column)}
    >
      <div className="header-content">
        {children}
        <span className="sort-indicator">
          {sortColumn === column && (
            sortDirection === 'asc' ? ' ↑' : ' ↓'
          )}
        </span>
      </div>
    </th>
  );

  if (reviews.length === 0) {
    return (
      <div className="dense-review-list empty">
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No reviews found</h3>
          <p>Try adjusting your filters or sync new reviews</p>
        </div>
      </div>
    );
  }

  // Bulk Actions Bar
  const BulkActionsBar = () => (
    selectedIds.size > 0 && (
      <div className="bulk-actions-bar">
        <span className="selection-count">
          {selectedIds.size} review{selectedIds.size > 1 ? 's' : ''} selected
        </span>
        <div className="bulk-actions">
          <button 
            className="bulk-action-btn"
            onClick={() => handleBulkAction('export')}
          >
            Export Selected
          </button>
          <button 
            className="bulk-action-btn"
            onClick={() => handleBulkAction('markResponded')}
          >
            Mark as Responded
          </button>
          <button 
            className="bulk-action-btn"
            onClick={() => handleBulkAction('setPriority')}
          >
            Set Priority
          </button>
          <button 
            className="bulk-action-btn danger"
            onClick={() => handleBulkAction('delete')}
          >
            Delete Selected
          </button>
        </div>
      </div>
    )
  );

  // Table View
  if (viewMode === 'table') {
    return (
      <div className="dense-review-list table-view">
        <BulkActionsBar />
        {selectedReview && (
          <ReviewDetailModal 
            review={selectedReview} 
            onClose={() => setSelectedReview(null)}
            onUpdate={onUpdate}
          />
        )}
        <div className="review-table-container">
          <table className="review-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === reviews.length && reviews.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <SortableHeader column="date">Date</SortableHeader>
                <SortableHeader column="source">Source</SortableHeader>
                <SortableHeader column="rating">Rating</SortableHeader>
                <SortableHeader column="reviewer">Reviewer</SortableHeader>
                <th className="review-col">Review</th>
                <SortableHeader column="highlights">Highlights</SortableHeader>
                <SortableHeader column="priority">Priority</SortableHeader>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedReviews.map(review => (
                <React.Fragment key={review.id}>
                  <tr 
                    className={`${selectedIds.has(review.id) ? 'selected' : ''} clickable-row`}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (!target.closest('input[type="checkbox"]') && 
                          !target.closest('.action-buttons') && 
                          !target.closest('.expand-btn')) {
                        setSelectedReview(review);
                      }
                    }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(review.id)}
                        onChange={() => toggleSelect(review.id)}
                      />
                    </td>
                    <td className="date-cell">
                      {formatDate(review.timestamp)}
                    </td>
                    <td className="source-cell">
                      <SourceBadge source={review.source} />
                    </td>
                    <td className="rating-cell">
                      <span className="stars" title={`${review.rating} stars`}>
                        {renderStars(review.rating)}
                      </span>
                    </td>
                    <td className="reviewer-cell">
                      {review.reviewer_name || 'Anonymous'}
                    </td>
                    <td className="review-cell">
                      <div className="review-text">
                        {expandedIds.has(review.id) 
                          ? review.review 
                          : truncateText(review.review, 150)}
                      </div>
                      {review.review.length > 150 && (
                        <button
                          className="expand-btn"
                          onClick={() => toggleExpand(review.id)}
                        >
                          {expandedIds.has(review.id) ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </td>
                    <td className="highlights-cell">
                      <HighlightCountBadge count={review.highlights?.length || 0} />
                    </td>
                    <td className="priority-cell">
                      <PriorityBadge priority={review.metadata?.priority} />
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="action-btn edit"
                          onClick={() => onUpdate(review.id, {})}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => onDelete(review.id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedIds.has(review.id) && review.ai_summary && (
                    <tr className="expanded-row">
                      <td colSpan={9}>
                        <div className="expanded-content">
                          <div className="ai-summary">
                            <strong>AI Summary:</strong> {review.ai_summary}
                          </div>
                          {review.suggested_response && (
                            <div className="suggested-response">
                              <strong>Suggested Response:</strong> {review.suggested_response}
                            </div>
                          )}
                          {review.key_themes && review.key_themes.length > 0 && (
                            <div className="themes">
                              <strong>Key Themes:</strong>
                              {review.key_themes.map((theme, i) => (
                                <span key={i} className="theme-tag">{theme}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Compact Card View
  if (viewMode === 'compact') {
    return (
      <div className="dense-review-list compact-view">
        <BulkActionsBar />
        <div className="compact-grid">
          {reviews.map(review => (
            <div key={review.id} className={`compact-card ${selectedIds.has(review.id) ? 'selected' : ''}`}>
              <div className="compact-header">
                <input
                  type="checkbox"
                  checked={selectedIds.has(review.id)}
                  onChange={() => toggleSelect(review.id)}
                />
                <span className="compact-date">{formatDate(review.timestamp)}</span>
                <span className={`source-badge source-${review.source.toLowerCase()}`}>
                  {review.source}
                </span>
                <span className="compact-rating">{renderStars(review.rating)}</span>
                <PriorityBadge priority={review.metadata?.priority} />
              </div>
              <div className="compact-body">
                <div className="compact-reviewer">
                  <strong>{review.reviewer_name || 'Anonymous'}</strong>
                </div>
                <div className="compact-text">
                  {expandedIds.has(review.id) 
                    ? review.review 
                    : truncateText(review.review, 100)}
                </div>
                {review.review.length > 100 && (
                  <button
                    className="expand-link"
                    onClick={() => toggleExpand(review.id)}
                  >
                    {expandedIds.has(review.id) ? 'Show less' : 'Show more'}
                  </button>
                )}
                {review.key_themes && review.key_themes.length > 0 && (
                  <div className="compact-themes">
                    {review.key_themes.slice(0, 3).map((theme, i) => (
                      <span key={i} className="mini-theme">{theme}</span>
                    ))}
                    {review.key_themes.length > 3 && (
                      <span className="more-themes">+{review.key_themes.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="compact-actions">
                <button onClick={() => onUpdate(review.id, {})}>Edit</button>
                <button onClick={() => onDelete(review.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Card View
  return (
    <div className="dense-review-list card-view">
      <BulkActionsBar />
      {selectedReview && (
        <ReviewDetailModal 
          review={selectedReview} 
          onClose={() => setSelectedReview(null)}
          onUpdate={onUpdate}
        />
      )}
      <div className="cards-grid">
        {sortedReviews.map(review => (
          <div 
            key={review.id} 
            className={`review-card ${selectedIds.has(review.id) ? 'selected' : ''}`}
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (!target.closest('input[type="checkbox"]') && 
                  !target.closest('.card-actions')) {
                setSelectedReview(review);
              }
            }}
          >
            <div className="card-header">
              <input
                type="checkbox"
                checked={selectedIds.has(review.id)}
                onChange={() => toggleSelect(review.id)}
                onClick={(e) => e.stopPropagation()}
              />
              <SourceBadge source={review.source} />
              <span className="card-date">{formatDate(review.timestamp)}</span>
            </div>
            
            <div className="card-body">
              <div className="card-reviewer">
                <strong>{review.reviewer_name || 'Anonymous'}</strong>
                <span className="card-rating">{renderStars(review.rating)}</span>
              </div>
              
              <div className="card-review">
                {expandedIds.has(review.id) 
                  ? review.review 
                  : truncateText(review.review, 200)}
              </div>
              
              {review.review.length > 200 && (
                <button
                  className="expand-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(review.id);
                  }}
                >
                  {expandedIds.has(review.id) ? 'Show less' : 'Show more'}
                </button>
              )}
              
              <div className="card-badges">
                <HighlightCountBadge count={review.highlights?.length || 0} />
                <PriorityBadge priority={review.metadata?.priority} />
                {review.metadata?.impact_score && (
                  <ImpactScoreBadge score={review.metadata.impact_score} />
                )}
              </div>
              
              {review.key_themes && review.key_themes.length > 0 && (
                <div className="card-themes">
                  {review.key_themes.slice(0, 3).map((theme, i) => (
                    <span key={i} className="theme-tag">{theme}</span>
                  ))}
                </div>
              )}
            </div>
            
            <div className="card-actions">
              <button
                className="action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdate(review.id, {});
                }}
                title="Edit"
              >
                ✏️
              </button>
              <button
                className="action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(review.id);
                }}
                title="Delete"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DenseReviewList;